/**
 * TelegramConfigCard - Configuración de alertas via Telegram
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send,
  Bell,
  BellOff,
  Settings,
  Save,
  TestTube,
  Loader2,
  AlertCircle,
  CheckCircle,
  Battery,
  Signal,
  Radio,
  Wifi,
  WifiOff,
  Info,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function TelegramConfigCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [botInfo, setBotInfo] = useState(null);
  
  const [config, setConfig] = useState({
    telegram_chat_id: '',
    alerts_enabled: false,
    alert_rf_drop: true,
    alert_battery_low: true,
    alert_interference: true,
    alert_device_offline: true,
    rf_threshold_dbm: -70,
    battery_threshold_percent: 20,
  });

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
    fetchBotInfo();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/telegram/config`, getAuthHeaders());
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      // Silent fail - config might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const fetchBotInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/telegram/bot-info`);
      setBotInfo(response.data);
    } catch (error) {
      // Silent fail
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/telegram/config`, config, getAuthHeaders());
      toast.success('Configuración de Telegram guardada');
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const sendTestAlert = async () => {
    if (!config.telegram_chat_id) {
      toast.error('Ingresa tu Chat ID primero');
      return;
    }
    
    setTesting(true);
    try {
      await axios.post(`${API_BASE}/api/telegram/test`, {}, getAuthHeaders());
      toast.success('Alerta de prueba enviada. Revisa tu Telegram.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error enviando alerta';
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#111111] border-white/[0.08]">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#71717A]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#111111] border-white/[0.08]">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger className="w-full text-left">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0088CC]/20 flex items-center justify-center">
                  <Send className="w-5 h-5 text-[#0088CC]" />
                </div>
                <div>
                  <CardTitle className="text-lg text-[#F4F4F5]">
                    Alertas por Telegram
                  </CardTitle>
                  <CardDescription className="text-[#71717A]">
                    Recibe notificaciones RF críticas al instante
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {config.alerts_enabled && config.telegram_chat_id ? (
                  <Badge className="bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30">
                    <Bell className="w-3 h-3 mr-1" /> Activo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[#71717A]">
                    <BellOff className="w-3 h-3 mr-1" /> Inactivo
                  </Badge>
                )}
              </div>
            </div>
          </CollapsibleTrigger>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-6">
            {/* Bot Status */}
            {botInfo && (
              <div className={`p-3 rounded-lg border ${
                botInfo.configured 
                  ? 'bg-[#0088CC]/10 border-[#0088CC]/30' 
                  : 'bg-[#FF3B30]/10 border-[#FF3B30]/30'
              }`}>
                {botInfo.configured ? (
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0088CC] mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-[#F4F4F5]">
                        Bot: @{botInfo.bot_username}
                      </p>
                      <p className="text-sm text-[#71717A] mt-1">
                        {botInfo.instructions}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#FF3B30]">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">{botInfo.message}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Chat ID Input */}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Chat ID de Telegram</Label>
              <div className="flex gap-2">
                <Input
                  value={config.telegram_chat_id}
                  onChange={(e) => setConfig(prev => ({ ...prev, telegram_chat_id: e.target.value }))}
                  placeholder="Ej: 123456789"
                  className="input-tactical"
                  data-testid="telegram-chat-id-input"
                />
                <Button
                  onClick={sendTestAlert}
                  disabled={testing || !config.telegram_chat_id}
                  variant="outline"
                  className="shrink-0"
                  data-testid="telegram-test-btn"
                >
                  {testing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-[#52525B]">
                Inicia chat con el bot y envía /start para obtener tu Chat ID
              </p>
            </div>
            
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-[#F4F4F5]" />
                <div>
                  <p className="font-medium text-[#F4F4F5]">Activar Alertas</p>
                  <p className="text-xs text-[#71717A]">Recibir notificaciones de eventos RF</p>
                </div>
              </div>
              <Switch
                checked={config.alerts_enabled}
                onCheckedChange={(checked) => setConfig(prev => ({ ...prev, alerts_enabled: checked }))}
                data-testid="telegram-alerts-toggle"
              />
            </div>
            
            {/* Alert Types */}
            {config.alerts_enabled && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-[#A1A1AA]">Tipos de Alerta</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* RF Drop */}
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                    <div className="flex items-center gap-2">
                      <Signal className="w-4 h-4 text-[#FF3B30]" />
                      <span className="text-sm text-[#F4F4F5]">Caída de Señal RF</span>
                    </div>
                    <Switch
                      checked={config.alert_rf_drop}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, alert_rf_drop: checked }))}
                    />
                  </div>
                  
                  {/* Battery */}
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                    <div className="flex items-center gap-2">
                      <Battery className="w-4 h-4 text-[#FFCC00]" />
                      <span className="text-sm text-[#F4F4F5]">Batería Baja</span>
                    </div>
                    <Switch
                      checked={config.alert_battery_low}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, alert_battery_low: checked }))}
                    />
                  </div>
                  
                  {/* Interference */}
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                    <div className="flex items-center gap-2">
                      <Radio className="w-4 h-4 text-[#AF52DE]" />
                      <span className="text-sm text-[#F4F4F5]">Interferencia</span>
                    </div>
                    <Switch
                      checked={config.alert_interference}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, alert_interference: checked }))}
                    />
                  </div>
                  
                  {/* Device Offline */}
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-white/[0.05]">
                    <div className="flex items-center gap-2">
                      <WifiOff className="w-4 h-4 text-[#FF3B30]" />
                      <span className="text-sm text-[#F4F4F5]">Dispositivo Offline</span>
                    </div>
                    <Switch
                      checked={config.alert_device_offline}
                      onCheckedChange={(checked) => setConfig(prev => ({ ...prev, alert_device_offline: checked }))}
                    />
                  </div>
                </div>
                
                {/* Thresholds */}
                <div className="space-y-4 pt-4 border-t border-white/[0.05]">
                  <h4 className="text-sm font-medium text-[#A1A1AA]">Umbrales de Alerta</h4>
                  
                  {/* RF Threshold */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#71717A]">Umbral RF mínimo</Label>
                      <span className="text-sm font-mono text-[#FF3B30]">{config.rf_threshold_dbm} dBm</span>
                    </div>
                    <Slider
                      value={[config.rf_threshold_dbm]}
                      onValueChange={([value]) => setConfig(prev => ({ ...prev, rf_threshold_dbm: value }))}
                      min={-100}
                      max={-40}
                      step={5}
                      className="w-full"
                    />
                  </div>
                  
                  {/* Battery Threshold */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[#71717A]">Umbral Batería mínimo</Label>
                      <span className="text-sm font-mono text-[#FFCC00]">{config.battery_threshold_percent}%</span>
                    </div>
                    <Slider
                      value={[config.battery_threshold_percent]}
                      onValueChange={([value]) => setConfig(prev => ({ ...prev, battery_threshold_percent: value }))}
                      min={5}
                      max={50}
                      step={5}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Save Button */}
            <Button
              onClick={saveConfig}
              disabled={saving}
              className="w-full bg-[#0088CC] hover:bg-[#0088CC]/90 text-white"
              data-testid="telegram-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Configuración
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
